import { theme } from 'antd';
import {
  type CSSProperties,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './PressRipple.module.css';

const reducedMotionQuery = '(prefers-reduced-motion: reduce)';

type RippleState = {
  id: number;
  state: 'pressed' | 'released';
  target: HTMLElement;
  size: number;
  x: number;
  y: number;
};

function findRippleTarget(target: EventTarget | null, selector: string) {
  const item = target instanceof Element ? target.closest(selector) : null;
  if (
    !(item instanceof HTMLElement) ||
    item.getAttribute('aria-disabled') === 'true' ||
    item.matches(':disabled')
  ) {
    return null;
  }
  return item;
}

function RippleLayer({
  ripple,
  onFinish,
}: {
  ripple: RippleState;
  onFinish: (id: number) => void;
}) {
  const layerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) {
      return;
    }
    const finishCancelledRipple = () => onFinish(ripple.id);
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finishReducedMotionRipple = () => {
      if (motion.matches) onFinish(ripple.id);
    };
    layer.addEventListener('animationcancel', finishCancelledRipple);
    // A preference change can hide a held ripple without a layer animation
    // ending. Subscribe only while a ripple exists, including the mount race.
    motion.addEventListener('change', finishReducedMotionRipple);
    finishReducedMotionRipple();
    return () => {
      layer.removeEventListener('animationcancel', finishCancelledRipple);
      motion.removeEventListener('change', finishReducedMotionRipple);
    };
  }, [onFinish, ripple.id]);

  useEffect(() => {
    if (ripple.state !== 'released') return;
    // Animation events may be skipped when the target is hidden mid-release.
    const timeout = window.setTimeout(() => onFinish(ripple.id), 1000);
    return () => window.clearTimeout(timeout);
  }, [onFinish, ripple.id, ripple.state]);

  return createPortal(
    <span
      aria-hidden
      className={styles.layer}
      data-ripple-state={ripple.state}
      onAnimationEnd={(event) => {
        if (event.target === event.currentTarget && ripple.state === 'released') {
          onFinish(ripple.id);
        }
      }}
      ref={layerRef}
    >
      <span
        className={styles.ripple}
        data-rippling="true"
        data-ripple-state={ripple.state}
        style={
          {
            '--press-ripple-size': `${ripple.size}px`,
            '--press-ripple-x': `${ripple.x}px`,
            '--press-ripple-y': `${ripple.y}px`,
          } as CSSProperties
        }
      />
    </span>,
    ripple.target,
  );
}

export function PressRipple({
  children,
  targetSelector = '[role="menuitem"]',
  resetKey,
}: PropsWithChildren<{ targetSelector?: string; resetKey?: string | boolean }>) {
  const { token } = theme.useToken();
  const [ripples, setRipples] = useState<RippleState[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const hasRipples = ripples.length > 0;
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !hasRipples) return;
    const pruneRemovedTargets = () => {
      setRipples((current) => {
        const connected = current.filter((item) => root.contains(item.target));
        return connected.length === current.length ? current : connected;
      });
    };
    // Observe only this ripple scope, and only while a ripple exists.
    const observer = new MutationObserver(pruneRemovedTargets);
    observer.observe(root, { childList: true, subtree: true });
    pruneRemovedTargets();
    return () => observer.disconnect();
  }, [hasRipples]);
  const previousResetKey = useRef(resetKey);
  useLayoutEffect(() => {
    if (previousResetKey.current !== resetKey) {
      previousResetKey.current = resetKey;
      setRipples([]);
    }
  }, [resetKey]);
  useEffect(() => {
    const reducedMotion = window.matchMedia(reducedMotionQuery);
    const clearRipples = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setRipples([]);
      }
    };
    reducedMotion.addEventListener('change', clearRipples);
    return () => reducedMotion.removeEventListener('change', clearRipples);
  }, []);
  const nextId = useRef(0);
  const activeRipple = ripples.find((ripple) => ripple.state === 'pressed');

  const showRipple = (
    target: HTMLElement,
    pointer?: { clientX: number; clientY: number },
  ) => {
    if (window.matchMedia(reducedMotionQuery).matches) {
      return;
    }

    const rect = target.getBoundingClientRect();
    const x = pointer ? pointer.clientX - rect.left : rect.width / 2;
    const y = pointer ? pointer.clientY - rect.top : rect.height / 2;
    // A press near an edge needs to reach the opposite corner, not just the
    // longest side. Round outward to avoid a subpixel seam at the final radius.
    const radius = Math.hypot(
      Math.max(Math.abs(x), Math.abs(rect.width - x)),
      Math.max(Math.abs(y), Math.abs(rect.height - y)),
    );
    const ripple: RippleState = {
      id: nextId.current++,
      state: 'pressed',
      target,
      size: Math.ceil(radius) * 2 + 2,
      x,
      y,
    };
    setRipples((current) => [
      ...current.map(
        (item): RippleState =>
          item.state === 'pressed' ? { ...item, state: 'released' } : item,
      ),
      ripple,
    ]);
  };

  const releaseRipple = () => {
    setRipples((current) =>
      current.some((item) => item.state === 'pressed')
        ? current.map((item) =>
            item.state === 'pressed' ? { ...item, state: 'released' as const } : item,
          )
        : current,
    );
  };

  const finishRipple = useCallback((id: number) => {
    setRipples((current) => current.filter((item) => item.id !== id));
  }, []);

  return (
    <div
      ref={rootRef}
      className={styles.root}
      style={
        {
          '--press-ripple-hover-bg': token.colorBgTextHover,
          '--press-ripple-button-hover-bg': token.colorFillTertiary,
          '--press-ripple-static-bg': token.colorFillSecondary,
        } as CSSProperties
      }
      onBlurCapture={(event) => {
        if (activeRipple?.target.contains(event.target)) {
          releaseRipple();
        }
      }}
      onKeyDownCapture={(event) => {
        const target = findRippleTarget(event.target, targetSelector);
        if (
          target &&
          !event.defaultPrevented &&
          !event.repeat &&
          (event.key === 'Enter' || event.key === ' ')
        ) {
          showRipple(target);
        }
      }}
      onKeyUpCapture={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          releaseRipple();
        }
      }}
      onPointerCancelCapture={releaseRipple}
      onPointerDownCapture={(event) => {
        const target = findRippleTarget(event.target, targetSelector);
        if (target && !event.defaultPrevented && event.button === 0) {
          showRipple(target, event);
        }
      }}
      onPointerOutCapture={(event) => {
        if (
          activeRipple &&
          (!(event.relatedTarget instanceof Node) ||
            !activeRipple.target.contains(event.relatedTarget))
        ) {
          releaseRipple();
        }
      }}
      onPointerUpCapture={releaseRipple}
    >
      {children}
      {ripples.map((ripple) => (
        <RippleLayer key={ripple.id} ripple={ripple} onFinish={finishRipple} />
      ))}
    </div>
  );
}
